<script setup lang="ts">
import { useStoreApp } from '@/stores/app'
import ConfirmDialogHost from '@/shared/components/confirm/ConfirmDialogHost.vue'
import TooltipHost from '@/shared/components/TooltipHost.vue'
import { useColorMode } from '@vueuse/core'
import { computed, onBeforeMount, ref, watch } from 'vue'
import 'vue-sonner/style.css'
import { Toaster } from '@/components/ui/sonner'
import SessionExpiringDialog from '@/app/components/auth/SessionExpiringDialog.vue'
import AppVersionReloadDialog from '@/app/components/app/AppVersionReloadDialog.vue'

import type { RuntimeMode } from '@/shared/types/app.type'

const storeApp = useStoreApp()
const appWrapper = ref<HTMLDivElement>()

// Applies the persisted theme class to <html> for every route, including
// select-organization/no-access which render outside the (with-statusbar)
// layout that the user menu's theme picker otherwise relies on.
// colorMode itself is the resolved light/dark; colorMode.store holds the raw
// auto/light/dark preference.
const colorMode = useColorMode()

// Sonner only applies its dark close-button rules under data-sonner-theme="dark"; left
// on its default "light" it paints a near-black X on our dark popover surface. Without
// emitAuto the ref only ever resolves to light/dark, but its type still admits auto.
const toasterTheme = computed(() => (colorMode.value === 'auto' ? 'system' : colorMode.value))

// Mirrors the preference to a cookie for the Keycloak login page. localStorage is
// origin-scoped and the login page is a different origin (kc.turing.no in deployed
// environments, another port locally), while cookies are domain-scoped and ignore the
// port — so this is the only store that spans both. Write-only: the app itself always
// reads localStorage, so the two cannot disagree.
watch(
  colorMode.store,
  (preference) => {
    const { hostname, protocol } = window.location
    // Shared across subdomains once deployed; omitted locally, where Domain= would be
    // rejected for localhost and the port-insensitivity of cookies already suffices.
    const domain = hostname.endsWith('turing.no') ? '; Domain=turing.no' : ''
    // Safari rejects Secure cookies over plain http even on localhost
    const secure = protocol === 'https:' ? '; Secure' : ''
    // Raw preference, not resolved — the login page resolves auto against
    // prefers-color-scheme itself, so both ends follow the OS the same way.
    document.cookie = `turing-color-scheme=${preference}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`
  },
  { immediate: true }
)

onBeforeMount(() => {
  storeApp.setEnv({
    api_scheme: import.meta.env.VITE_APISCHEME as string,
    api_host: import.meta.env.VITE_APIHOST as string,
    api_port: import.meta.env.VITE_APIPORT as string,
    api_path: import.meta.env.VITE_APIPATH as string,
    tiles_url: import.meta.env.VITE_TILES_URL as string,
    mode: import.meta.env.VITE_MODE as RuntimeMode,
    site_name: import.meta.env.VITE_SITE_NAME as string
  })
})
</script>

<template>
  <div
    id="pagewrapper"
    class="flex h-full w-full"
    ref="appWrapper"
  >
    <Toaster
      position="bottom-right"
      :theme="toasterTheme"
      close-button
    />
    <ConfirmDialogHost />
    <TooltipHost />
    <SessionExpiringDialog />
    <AppVersionReloadDialog />
    <router-view />
  </div>
</template>
